import { useState, useEffect, useRef } from 'react';

/**
 * Hook for detecting browser connectivity with debounce.
 * Uses navigator.onLine + window online/offline events.
 * 2-second debounce before declaring offline to avoid UI thrashing
 * on intermittent WiFi.
 */
export default function useConnectivity() {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [lastOnlineAt, setLastOnlineAt] = useState(() =>
    navigator.onLine ? Date.now() : null
  );
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const handleOnline = () => {
      // Clear any pending offline debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Go online immediately (no debounce needed)
      setIsOnline(true);
      setLastOnlineAt(Date.now());
    };

    const handleOffline = () => {
      // Debounce going offline by 2 seconds to prevent flicker
      debounceTimerRef.current = setTimeout(() => {
        setIsOnline(false);
        debounceTimerRef.current = null;
      }, 2000);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Update lastOnlineAt periodically while online
  useEffect(() => {
    if (!isOnline) return;
    const interval = setInterval(() => {
      setLastOnlineAt(Date.now());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [isOnline]);

  return { isOnline, lastOnlineAt };
}
